import {getReasonText} from '@Libraries/font-load-error.library';
import {asyncSleep} from '@Utils/misc.util';

export interface FontFaceLoadSteps {
  /** Settles once the family's stylesheet has been parsed, so its @font-face rules exist; rejects if it failed to load. */
  waitForStyleSheet: () => Promise<void>;
  /** Starts the browser's own load of the family's faces and resolves with the faces that matched. */
  loadFaces: () => Promise<ArrayLike<unknown>>;
}

export const FONT_LOAD_TICK_MS = 1000;
export const FONT_LOAD_RESPONSIVE_TIMEOUT_TICKS = 60;
export const MAX_REMATCH_DELAY_MS = 250;
const FIRST_REMATCH_DELAY_MS = 25;
const REMATCH_DELAY_GROWTH_FACTOR = 2;
const RESPONSIVE_BUDGET_DESCRIPTION = `${FONT_LOAD_RESPONSIVE_TIMEOUT_TICKS}s of responsive page time`;

/**
 * Loads one font family and resolves when the browser reports its faces loaded.
 *
 * The budget is counted in one-second ticks rather than wall-clock time. A tick only elapses while
 * the page is responsive, so a main thread blocked by poster initialisation, or a tab the browser has
 * frozen or throttled, consumes at most one tick however long it lasted; the font then loads on resume
 * instead of being refused for a timeout that ran out while nothing could run.
 */
export const loadFontFace = (fontFamily: string, steps: FontFaceLoadSteps): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    const faceMatcher = new FaceMatcher(steps.loadFaces);
    const cancelBudget = startResponsiveBudget(() => {
      faceMatcher.stop();
      reject(new Error(faceMatcher.isAwaitingActivation() ? getNoFaceMatchedReason(fontFamily) : getNotLoadedReason(fontFamily)));
    });

    steps
      .waitForStyleSheet()
      .then(() => faceMatcher.matchFaces())
      .then(resolve)
      .catch((reason: unknown) => {
        reject(toError(reason));
      })
      .finally(() => {
        faceMatcher.stop();
        cancelBudget();
      });
  });
};

/**
 * WebKit keeps a loaded stylesheet's @font-face rules inactive while any other stylesheet on the page is
 * still loading, so document.fonts.load() matches no face until the slowest of those arrives. An empty
 * match therefore means "not active yet", not "missing": match again with backoff until a face matches
 * or the load is stopped, leaving the responsive budget as the only limit on how long that may take.
 */
class FaceMatcher {
  readonly #loadFaces: FontFaceLoadSteps['loadFaces'];
  #isStopped = false;
  #isAwaitingActivation = false;

  constructor(loadFaces: FontFaceLoadSteps['loadFaces']) {
    this.#loadFaces = loadFaces;
  }

  /** Settles once a face matches, or quietly once stopped; the caller has settled the load by then. */
  public async matchFaces(rematchDelayMs = FIRST_REMATCH_DELAY_MS): Promise<void> {
    this.#isAwaitingActivation = false;
    const faces = await this.#loadFaces();
    if (faces.length > 0 || this.#isStopped) {
      return;
    }
    this.#isAwaitingActivation = true;
    await asyncSleep(rematchDelayMs);
    if (this.#isStopped) {
      return;
    }
    await this.matchFaces(Math.min(rematchDelayMs * REMATCH_DELAY_GROWTH_FACTOR, MAX_REMATCH_DELAY_MS));
  }

  /** True between an empty match and the next attempt: the stylesheet loaded but none of its faces is active. */
  public isAwaitingActivation(): boolean {
    return this.#isAwaitingActivation;
  }

  public stop(): void {
    this.#isStopped = true;
  }
}

/** The budget ran out while none of the family's faces had become active. */
const getNoFaceMatchedReason = (fontFamily: string): string => {
  return `"${fontFamily}" matched no @font-face rule within ${RESPONSIVE_BUDGET_DESCRIPTION}`;
};

/** The budget ran out while a matched face was still loading. */
const getNotLoadedReason = (fontFamily: string): string => {
  return `"${fontFamily}" did not load within ${RESPONSIVE_BUDGET_DESCRIPTION}`;
};

/** Each tick is armed only when the previous one has run, so a long stall advances the budget by one tick, not by its length. */
const startResponsiveBudget = (onExhausted: () => void): (() => void) => {
  let ticksElapsed = 0;
  let tickTimer: ReturnType<typeof setTimeout>;
  const onTick = (): void => {
    ticksElapsed += 1;
    if (ticksElapsed >= FONT_LOAD_RESPONSIVE_TIMEOUT_TICKS) {
      onExhausted();
      return;
    }
    tickTimer = setTimeout(onTick, FONT_LOAD_TICK_MS);
  };
  tickTimer = setTimeout(onTick, FONT_LOAD_TICK_MS);
  return (): void => {
    clearTimeout(tickTimer);
  };
};

const toError = (reason: unknown): Error => {
  return reason instanceof Error ? reason : new Error(getReasonText(reason));
};
